import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import betslip, { Betslip as BetslipTypes } from '@gin-betting/betslip';
import core, { App, GQL } from '@gin-betting/core';
import { OrderedSet, Seq } from 'immutable';
import { includes } from 'lodash';

import { useSetDefaultStake } from '../../hooks';

const {
    utils: { getIdsFromBetslipOddId },
} = core;
const {
    constants: { BETSLIP_ODD_STATUSES_TO_PLACE_BET },
    actions: { changeBetslipMode },
    utils: { getBetSystemSizes },
} = betslip;

interface Input {
    betslipBetId: string;
    betslipBet: BetslipTypes.BetslipBetWithStatus;
}

interface Output {
    mode: GQL.BetType;
    isFooterDisabled: boolean;
    status: App.BetslipStatus;
    systemSize: number[];
    betslipOddsSize: number;
    betslipOddIdsGroup: Seq.Indexed<[string, OrderedSet<string>]>;
    disabledModes: GQL.BetType[];
    handleChangeMode: (activeTab: GQL.BetType) => void;
    isSingle: boolean;
}

const useBetslip = ({ betslipBetId, betslipBet }: Input): Output => {
    const dispatch = useDispatch();

    const { mode, betslipOddIds, status, systemSize } = betslipBet;
    const isSingle = mode === GQL.BetType.SINGLE;

    useSetDefaultStake({ betslipBetId });
    const betslipOddsSize = betslipOddIds.count();

    const handleChangeMode = useCallback(
        (betType: GQL.BetType) => {
            dispatch(
                changeBetslipMode({
                    betslipBetId,
                    mode: betType,
                })
            );
        },
        [dispatch, betslipBetId]
    );

    const isDisabled = !includes(BETSLIP_ODD_STATUSES_TO_PLACE_BET, status);

    const betslipOddIdsGroup = isSingle
        ? (betslipOddIds.entrySeq().map(([_, betslipOddId]) => {
              const { matchId } = getIdsFromBetslipOddId(betslipOddId);

              return [matchId, OrderedSet([betslipOddId])];
          }) as Seq.Indexed<[string, OrderedSet<string>]>)
        : (betslipOddIds
              .groupBy(betslipOddId => {
                  const { matchId } = getIdsFromBetslipOddId(betslipOddId);

                  return matchId;
              })
              .entrySeq() as Seq.Indexed<[string, OrderedSet<string>]>);

    const disabledModes = useMemo(() => {
        let result: GQL.BetType[] = [];

        if (isDisabled) {
            result = [
                GQL.BetType.SINGLE,
                GQL.BetType.EXPRESS,
                GQL.BetType.SYSTEM,
            ].filter(m => m !== mode);
        } else {
            if (betslipOddsSize >= 2) {
                result.push(GQL.BetType.SINGLE);
            }

            if (betslipOddsSize <= 1) {
                result.push(GQL.BetType.EXPRESS);
            }

            if (betslipOddsSize <= 2) {
                result.push(GQL.BetType.SYSTEM);
            }
        }

        return result;
    }, [isDisabled, betslipOddsSize, mode]);

    return {
        mode,
        status,
        isFooterDisabled: isDisabled && !isSingle,
        betslipOddIdsGroup,
        disabledModes,
        handleChangeMode,
        systemSize: getBetSystemSizes({
            betType: mode,
            selectionsCount: betslipOddsSize,
            systemSize,
        }),
        betslipOddsSize,
        isSingle,
    };
};

export default useBetslip;
